'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';


const supabase = createClient();

interface Conversazione {
  id: string;
  titolo: string;
  messaggi: Message[];
  contesto: string;
  created_at: string;
  updated_at: string;
}

interface Message {
  ruolo: 'utente' | 'assistente';
  testo: string;
  timestamp: string;
}

const CONTESTI = [
  { value: 'generale', label: 'Generale', icon: 'ChatBubbleLeftRightIcon' },
  { value: 'ticket', label: 'Ticket', icon: 'TicketIcon' },
  { value: 'dispositivi', label: 'Dispositivi', icon: 'ServerIcon' },
  { value: 'kb', label: 'Knowledge Base', icon: 'BookOpenIcon' },
  { value: 'rete', label: 'Rete', icon: 'GlobeAltIcon' },
];

const SUGGERIMENTI = [
  'Quanti dispositivi sono offline in questo momento?',
  'Mostrami i ticket aperti con priorità critica',
  'Come si configura una VLAN?',
  'Qual è la procedura per aggiungere un nuovo dispositivo?',
  'Mostrami le ultime anomalie di rete',
  'Come posso resettare la password di un dispositivo?',
];

export default function ChatbotContent() {
  const [conversazioni, setConversazioni] = useState<Conversazione[]>([]);
  const [selectedConv, setSelectedConv] = useState<Conversazione | null>(null);
  const [messaggi, setMessaggi] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingConv, setLoadingConv] = useState(true);
  const [contesto, setContesto] = useState('generale');
  const [thinking, setThinking] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { profile } = useAuth();

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => { scrollToBottom(); }, [messaggi]);

  const fetchConversazioni = useCallback(async () => {
    setLoadingConv(true);
    const { data } = await supabase
      .from('chatbot_conversazioni')
      .select('*')
      .order('updated_at', { ascending: false })
      .limit(20);
    setConversazioni(data || []);
    setLoadingConv(false);
  }, []);

  useEffect(() => { fetchConversazioni(); }, [fetchConversazioni]);

  const nuovaConversazione = () => {
    setSelectedConv(null);
    setMessaggi([]);
    setInput('');
  };

  const openConversazione = (conv: Conversazione) => {
    setSelectedConv(conv);
    setMessaggi(conv.messaggi || []);
    setContesto(conv.contesto || 'generale');
  };

  const buildSystemContext = async (): Promise<string> => {
    let ctx = `Sei NetBot, l'assistente AI interno di GestioneLab, un sistema di gestione IT per strutture sanitarie italiane.
Rispondi sempre in italiano. Sei preciso, professionale e conciso.
Contesto attuale: ${contesto}.
Data/ora: ${new Date().toLocaleString('it-IT')}.
Utente: ${profile?.full_name || profile?.email || 'Tecnico IT'} (ruolo: ${profile?.role || 'tecnico'}).
`;

    if (contesto === 'dispositivi') {
      const { data: devices } = await supabase.from('devices').select('nome, tipo, stato, ip_address, reparto').limit(20);
      if (devices?.length) ctx += `\nDispositivi nel sistema (ultimi 20):\n${devices.map(d => `- ${d.nome} (${d.tipo}) - ${d.stato} - IP: ${d.ip_address || 'N/A'} - Reparto: ${d.reparto || 'N/A'}`).join('\n')}`;
    } else if (contesto === 'ticket') {
      const { data: tickets } = await supabase.from('tickets').select('numero_ticket, titolo, stato, priorita, reparto').eq('stato', 'aperto').limit(15);
      if (tickets?.length) ctx += `\nTicket aperti:\n${tickets.map(t => `- ${t.numero_ticket}: ${t.titolo} (${t.priorita}) - Reparto: ${t.reparto || 'N/A'}`).join('\n')}`;
    } else if (contesto === 'rete') {
      const { data: allarmi } = await supabase.from('allarmi').select('titolo, severita, stato').eq('stato', 'attivo').limit(10);
      if (allarmi?.length) ctx += `\nAllarmi attivi:\n${allarmi.map(a => `- ${a.titolo} (${a.severita})`).join('\n')}`;
    }
    return ctx;
  };

  const sendMessage = async (testo?: string) => {
    const msg = (testo || input).trim();
    if (!msg) return;
    setInput('');

    const userMsg: Message = { ruolo: 'utente', testo: msg, timestamp: new Date().toISOString() };
    const newMsgs = [...messaggi, userMsg];
    setMessaggi(newMsgs);
    setThinking(true);

    try {
      const systemCtx = await buildSystemContext();
      const history = newMsgs.slice(-10).map(m => ({
        role: m.ruolo === 'utente' ? 'user' : 'assistant',
        content: m.testo,
      }));

      const response = await fetch('/api/chatbot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: history, system: systemCtx }),
      });

      let assistantText = '';
      if (response.ok) {
        const data = await response.json();
        assistantText = data.message || 'Risposta non disponibile.';
      } else {
        assistantText = 'Errore nella comunicazione con il chatbot. Verifica la configurazione API.';
      }

      const assistantMsg: Message = { ruolo: 'assistente', testo: assistantText, timestamp: new Date().toISOString() };
      const finalMsgs = [...newMsgs, assistantMsg];
      setMessaggi(finalMsgs);

      // Save to DB
      const titolo = selectedConv?.titolo || msg.slice(0, 50);
      if (selectedConv) {
        await supabase.from('chatbot_conversazioni').update({
          messaggi: finalMsgs,
          updated_at: new Date().toISOString(),
        }).eq('id', selectedConv.id);
      } else {
        const { data: newConv } = await supabase.from('chatbot_conversazioni').insert({
          utente_id: profile?.id,
          titolo,
          messaggi: finalMsgs,
          contesto,
        }).select().single();
        if (newConv) setSelectedConv(newConv);
      }
      fetchConversazioni();
    } catch (e) {
      const errMsg: Message = { ruolo: 'assistente', testo: 'Errore di connessione. Riprova.', timestamp: new Date().toISOString() };
      setMessaggi(prev => [...prev, errMsg]);
    } finally {
      setThinking(false);
    }
  };

  const deleteConversazione = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await supabase.from('chatbot_conversazioni').delete().eq('id', id);
    if (selectedConv?.id === id) nuovaConversazione();
    fetchConversazioni();
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-4">
      {/* Sidebar conversazioni */}
      <div className="w-64 flex-shrink-0 flex flex-col bg-surface-elevated border border-border rounded-xl overflow-hidden">
        <div className="p-3 border-b border-border">
          <button onClick={nuovaConversazione}
            className="w-full flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors">
            <Icon name="PlusIcon" size={16} /> Nuova Chat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {loadingConv ? (
            <div className="flex justify-center py-4"><div className="w-5 h-5 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>
          ) : conversazioni.length === 0 ? (
            <p className="text-xs text-muted-color text-center py-4">Nessuna conversazione</p>
          ) : conversazioni.map(conv => (
            <div key={conv.id}
              onClick={() => openConversazione(conv)}
              className={`group flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-colors ${selectedConv?.id === conv.id ? 'bg-sky-500/10 border border-sky-500/20' : 'hover:bg-surface'}`}>
              <Icon name="ChatBubbleLeftRightIcon" size={14} className="text-muted-color mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-white truncate">{conv.titolo || 'Conversazione'}</p>
                <p className="text-xs text-muted-color">{new Date(conv.updated_at).toLocaleDateString('it-IT')}</p>
              </div>
              <button onClick={e => deleteConversazione(conv.id, e)}
                className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:text-red-400 text-muted-color transition-all">
                <Icon name="TrashIcon" size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col bg-surface-elevated border border-border rounded-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center">
              <Icon name="CpuChipIcon" size={16} className="text-sky-400" />
            </div>
            <div>
              <p className="font-semibold text-white text-sm">NetBot</p>
              <p className="text-xs text-green-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" /> Online</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-color">Contesto:</span>
            <select value={contesto} onChange={e => setContesto(e.target.value)}
              className="bg-surface border border-border rounded-lg px-2 py-1 text-xs text-white">
              {CONTESTI.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messaggi.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-2xl bg-sky-500/10 flex items-center justify-center mb-4">
                <Icon name="CpuChipIcon" size={32} className="text-sky-400" />
              </div>
              <h3 className="text-white font-semibold mb-2">Ciao! Sono NetBot</h3>
              <p className="text-muted-color text-sm mb-6 max-w-sm">Il tuo assistente AI interno. Posso aiutarti con dispositivi, ticket, rete e knowledge base.</p>
              <div className="grid grid-cols-2 gap-2 w-full max-w-md">
                {SUGGERIMENTI.map((s, i) => (
                  <button key={i} onClick={() => sendMessage(s)}
                    className="text-left text-xs bg-surface hover:bg-surface-elevated border border-border rounded-lg p-2 text-muted-color hover:text-white transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messaggi.map((msg, i) => (
                <div key={i} className={`flex gap-3 ${msg.ruolo === 'utente' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold ${msg.ruolo === 'utente' ? 'bg-sky-600 text-white' : 'bg-surface border border-border text-sky-400'}`}>
                    {msg.ruolo === 'utente' ? (profile?.full_name?.[0] || 'U') : 'AI'}
                  </div>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm ${msg.ruolo === 'utente' ? 'bg-sky-600 text-white rounded-tr-sm' : 'bg-surface border border-border text-white rounded-tl-sm'}`}>
                    <p className="whitespace-pre-wrap leading-relaxed">{msg.testo}</p>
                    <p className={`text-xs mt-1 ${msg.ruolo === 'utente' ? 'text-sky-200' : 'text-muted-color'}`}>
                      {new Date(msg.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              {thinking && (
                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-surface border border-border flex items-center justify-center text-xs text-sky-400">AI</div>
                  <div className="bg-surface border border-border rounded-2xl rounded-tl-sm px-4 py-3">
                    <div className="flex gap-1">
                      {[0, 1, 2].map(i => (
                        <div key={i} className="w-2 h-2 bg-sky-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="p-4 border-t border-border">
          <div className="flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Scrivi un messaggio... (Invio per inviare)"
              className="flex-1 bg-surface border border-border rounded-xl px-4 py-3 text-sm text-white placeholder-muted-color focus:outline-none focus:border-sky-500 transition-colors"
              disabled={thinking}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || thinking}
              className="bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white p-3 rounded-xl transition-colors"
            >
              <Icon name="PaperAirplaneIcon" size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
